import { Route } from "../../../types/global/types"
import {
  CreateRoute,
  GetRoutesPayload,
  GetRoutePayload,
  UpdateRouteTriggerAC,
} from "../../../types/reducers/routesReducer"
import { InferActionsTypes } from "./../index"

/** One (household, conflicting route) pair. A household on two routes yields two of these. */
export type ConflictEntry = {
  household_id: number
  household_name: string | null
  route_id: number
  route_name: string | null
  point_id: number
  /** True when Move here would strip that route of its last point, so it gets deleted. */
  would_empty_route: boolean
}

export type MoveConflict = {
  route_id: number
  route_name: string
  point_id: number
  conflicts?: ConflictEntry[]
}

/**
 * The write to retry once the user picks Move all here / Add all anyway. A conflict can
 * come from either route creation (map draft) or a route update (RoutePage), and the two
 * retry through different endpoints, so the mode travels with the payload.
 */
export type PendingRoutePayload =
  | { mode: "update"; id: number | undefined; data: UpdateRouteTriggerAC["data"] }
  | { mode: "create"; data: CreateRoute; draftId?: string }

export const actionTypes = {
  GET_ROUTES_TRIGGER: "GET_ROUTES_TRIGGER",
  GET_ROUTE_TRIGGER: "GET_ROUTE_TRIGGER",
  CREATE_ROUTE_TRIGGER: "CREATE_ROUTE_TRIGGER",
  UPDATE_ROUTE_TRIGGER: "UPDATE_ROUTE_TRIGGER",
  DELETE_ROUTE_TRIGGER: "DELETE_ROUTE_TRIGGER",
  BULK_DELETE_ROUTES_TRIGGER: "BULK_DELETE_ROUTES_TRIGGER",
  DELETE_ALL_ROUTES_TRIGGER: "DELETE_ALL_ROUTES_TRIGGER",
  GET_ROUTES: "GET_ROUTES",
  GET_ROUTE: "GET_ROUTE",
  CREATE_ROUTE: "CREATE_ROUTE",
  UPDATE_ROUTE: "UPDATE_ROUTE",
  MARK_POINT_VISITED: "MARK_POINT_VISITED",
  MARK_POINT_VISITED_SUCCESS: "MARK_POINT_VISITED_SUCCESS",
  MARK_POINT_VISITED_FAILED: "MARK_POINT_VISITED_FAILED",
  UNMARK_POINT_VISITED: "UNMARK_POINT_VISITED",
  UNMARK_POINT_VISITED_SUCCESS: "UNMARK_POINT_VISITED_SUCCESS",
  UNMARK_POINT_VISITED_FAILED: "UNMARK_POINT_VISITED_FAILED",
  SHOW_MOVE_CONFLICT: "SHOW_MOVE_CONFLICT",
  CLEAR_MOVE_CONFLICT: "CLEAR_MOVE_CONFLICT",
  FORCE_MOVE_POINT_TRIGGER: "FORCE_MOVE_POINT_TRIGGER",
  FORCE_ADD_POINT_TRIGGER: "FORCE_ADD_POINT_TRIGGER",
  CREATE_ROUTE_FAILED: "CREATE_ROUTE_FAILED",
  CLEAR_FAILED_DRAFT: "CLEAR_FAILED_DRAFT",
} as const

type InitialState = {
  routes: null | { data: Route | null }
  route: null | Route
  moveConflict: MoveConflict | null
  pendingUpdatePayload: PendingRoutePayload | null
  /** Draft id of a map route whose save failed, so the panel can restore it. */
  lastFailedDraftId: string | null
}

const initialState: InitialState = {
  routes: null,
  route: null,
  moveConflict: null,
  pendingUpdatePayload: null,
  lastFailedDraftId: null,
}

export const routesReducer = (state = initialState, action: Actions): InitialState => {
  switch (action.type) {
    case actionTypes.GET_ROUTES:
      return { ...state, routes: action.payload }
    case actionTypes.GET_ROUTE:
      return { ...state, route: action.payload }
    case actionTypes.MARK_POINT_VISITED:
      if (!state.route?.points) return state;
      return {
        ...state,
        route: {
          ...state.route,
          points: state.route.points.map((p: any) =>
            p.id === action.pointId ? { ...p, visited_at: new Date().toISOString(), _optimistic: true } : p
          ),
        },
      }
    case actionTypes.MARK_POINT_VISITED_SUCCESS:
      if (!state.route?.points) return state;
      return {
        ...state,
        route: {
          ...state.route,
          points: state.route.points.map((p: any) =>
            p.id === action.pointId ? { ...p, visited_at: action.visited_at, visited_by_user_id: action.visited_by_user_id, _optimistic: false } : p
          ),
        },
      }
    case actionTypes.MARK_POINT_VISITED_FAILED:
      if (!state.route?.points) return state;
      return {
        ...state,
        route: {
          ...state.route,
          points: state.route.points.map((p: any) =>
            p.id === action.pointId ? { ...p, visited_at: null, visited_by_user_id: null, _optimistic: false } : p
          ),
        },
      }
    case actionTypes.UNMARK_POINT_VISITED:
      if (!state.route?.points) return state;
      return {
        ...state,
        route: {
          ...state.route,
          points: state.route.points.map((p: any) =>
            p.id === action.pointId ? { ...p, visited_at: null, _optimistic: true } : p
          ),
        },
      }
    case actionTypes.UNMARK_POINT_VISITED_SUCCESS:
      if (!state.route?.points) return state;
      return {
        ...state,
        route: {
          ...state.route,
          points: state.route.points.map((p: any) =>
            p.id === action.pointId ? { ...p, visited_at: null, visited_by_user_id: null, _optimistic: false } : p
          ),
        },
      }
    case actionTypes.UNMARK_POINT_VISITED_FAILED:
      if (!state.route?.points) return state;
      return {
        ...state,
        route: {
          ...state.route,
          points: state.route.points.map((p: any) =>
            p.id === action.pointId ? { ...p, _optimistic: false } : p
          ),
        },
      }

    case actionTypes.SHOW_MOVE_CONFLICT:
      return {
        ...state,
        moveConflict: action.conflict,
        pendingUpdatePayload: action.pendingUpdatePayload,
      }

    case actionTypes.CLEAR_MOVE_CONFLICT:
      return {
        ...state,
        moveConflict: null,
        pendingUpdatePayload: null,
      }

    case actionTypes.CREATE_ROUTE_FAILED:
      return {
        ...state,
        lastFailedDraftId: action.draftId ?? null,
      }

    case actionTypes.CLEAR_FAILED_DRAFT:
      return {
        ...state,
        lastFailedDraftId: null,
      }

    default:
      return state
  }
}

export const actions = {
  getRoutesAC: (data: { data: Route | null }) => ({
    type: actionTypes.GET_ROUTES,
    payload: data,
  }),

  getRouteAC: (data: Route | null) => ({
    type: actionTypes.GET_ROUTE,
    payload: data,
  }),
  // getRouteAC: (data: Route | null) => ({
  //   type: actionTypes.GET_ROUTE,
  //   payload: data,
  // }),

  markPointVisitedSuccessAC: (pointId: number, visited_at: string, visited_by_user_id: number) => ({
    type: actionTypes.MARK_POINT_VISITED_SUCCESS,
    pointId,
    visited_at,
    visited_by_user_id,
  }),
  markPointVisitedFailedAC: (pointId: number) => ({
    type: actionTypes.MARK_POINT_VISITED_FAILED,
    pointId,
  }),
  unmarkPointVisitedSuccessAC: (pointId: number) => ({
    type: actionTypes.UNMARK_POINT_VISITED_SUCCESS,
    pointId,
  }),
  unmarkPointVisitedFailedAC: (pointId: number) => ({
    type: actionTypes.UNMARK_POINT_VISITED_FAILED,
    pointId,
  }),
  markPointVisitedAC: (pointId: number) => ({
    type: actionTypes.MARK_POINT_VISITED,
    pointId,
  }),
  unmarkPointVisitedAC: (pointId: number) => ({
    type: actionTypes.UNMARK_POINT_VISITED,
    pointId,
  }),
  showMoveConflictAC: (conflict: MoveConflict, pendingUpdatePayload: PendingRoutePayload) => ({
    type: actionTypes.SHOW_MOVE_CONFLICT,
    conflict,
    pendingUpdatePayload,
  }),
  clearMoveConflictAC: () => ({
    type: actionTypes.CLEAR_MOVE_CONFLICT,
  }),
  createRouteFailedAC: (draftId?: string) => ({
    type: actionTypes.CREATE_ROUTE_FAILED,
    draftId,
  }),
  clearFailedDraftAC: () => ({
    type: actionTypes.CLEAR_FAILED_DRAFT,
  }),
}

export const routesTriggerActions = {
  getRoutesTriggerAC: ({ household, address, page, perPage, searchValue }: GetRoutesPayload) => ({
    type: actionTypes.GET_ROUTES_TRIGGER,
    household,
    address,
    page,
    perPage,
    searchValue
  }),
  
  getRouteTriggerAC: ({ id, household, address }: GetRoutePayload) => ({
    type: actionTypes.GET_ROUTE_TRIGGER,
    household,
    address,
    id,
  }),
  createRouteTriggerAC: (data: CreateRoute, draftId?: string) => ({
    type: actionTypes.CREATE_ROUTE_TRIGGER,
    data,
    draftId,
  }),
  updateRouteTriggerAC: ({ id, data }: UpdateRouteTriggerAC) => ({
    type: actionTypes.UPDATE_ROUTE_TRIGGER,
    id,
    data,
  }),
  deleteRouteTriggerAC: (id: number | undefined) => ({
    type: actionTypes.DELETE_ROUTE_TRIGGER,
    id,
  }),
  bulkDeleteRoutesTriggerAC: (ids: number[]) => ({
    type: actionTypes.BULK_DELETE_ROUTES_TRIGGER,
    ids,
  }),
  deleteAllRoutesTriggerAC: () => ({
    type: actionTypes.DELETE_ALL_ROUTES_TRIGGER,
  }),

  markPointVisitedTriggerAC: (routeId: number, pointId: number) => ({
    type: actionTypes.MARK_POINT_VISITED,
    routeId,
    pointId,
  }),
  unmarkPointVisitedTriggerAC: (routeId: number, pointId: number) => ({
    type: actionTypes.UNMARK_POINT_VISITED,
    routeId,
    pointId,
  }),

  forceMovePointTriggerAC: (payload: PendingRoutePayload) => ({
    type: actionTypes.FORCE_MOVE_POINT_TRIGGER,
    payload,
  }),
  forceAddPointTriggerAC: (payload: PendingRoutePayload) => ({
    type: actionTypes.FORCE_ADD_POINT_TRIGGER,
    payload,
  }),
}

export type Actions = InferActionsTypes<typeof actions>
